/**
 * The Combine — gamified, sports-centric, endless AI practice (Tier 1 GameDay Skill Engine).
 * Real-time streaks & accolades ("He's on fire!"), coach hints, encouragement on misses,
 * and XP that feeds the school-side Mastery Radar.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Flame, Loader2, ArrowLeft, ArrowRight, Lightbulb, Trophy, Zap,
  Target, CheckCircle2, XCircle, RefreshCw, Dumbbell, BookOpen, FlaskConical, Users, Calendar,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const SUBJECTS = [
  { key: 'math', title: 'Math / Data Analytics', icon: Target, color: '#3B82F6', blurb: 'Stats, ratios, geometry — through the game.' },
  { key: 'ela', title: 'ELA / Persuasion', icon: BookOpen, color: '#8B5CF6', blurb: 'Reading, writing & brand voice like a pro.' },
  { key: 'science', title: 'Science / Biomechanics', icon: FlaskConical, color: '#10B981', blurb: 'The physics & biology of peak performance.' },
  { key: 'social_studies', title: 'Social Studies / Leadership', icon: Users, color: '#F59E0B', blurb: 'History, civics & captain-level decisions.' },
];

const CORRECT_CALLS = ['Nothing but net!', 'Boomshakalaka!', 'Bucket!', 'That\'s a dime!', 'Clean rep!', 'Textbook!'];
const MISS_CALLS = ['Shake it off — next possession.', 'Reset and attack again.', 'Champions bounce back. Run it.', 'Watch the coach hint, then counter.', 'No panic. Next rep wins it.'];
const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];

const fireText = (streak) => {
  if (streak >= 6) return 'UNCONSCIOUS! Get this scholar out of the gym!';
  if (streak >= 5) return 'RAZZLE DAZZLE! He literally cannot miss!';
  if (streak >= 3) return "HE'S ON FIRE! Can't miss right now!";
  if (streak === 2) return 'Heating up...';
  return '';
};

export default function CombinePractice() {
  const nav = useNavigate();
  const [phase, setPhase] = useState('select'); // select | loading | play | done
  const [mode, setMode] = useState('subject');
  const [subject, setSubject] = useState(null);
  const [subjectTitle, setSubjectTitle] = useState('');
  const [grade, setGrade] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null); // {correct, answer_index, explanation}
  const [showHint, setShowHint] = useState(false);
  const [busy, setBusy] = useState(false);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [accolade, setAccolade] = useState('');
  const [summary, setSummary] = useState(null);
  const [averages, setAverages] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [stateLabel, setStateLabel] = useState('');

  const fire = streak >= 3;

  const loadHistory = useCallback(() => {
    api.get('/academy/combine/history', { headers: auth() })
      .then((r) => setAverages(r.data.averages || [])).catch(() => {});
    api.get('/academy/combine/assignments', { headers: auth() })
      .then((r) => setAssignments(r.data.assignments || [])).catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const run = async ({ m, subj }) => {
    setPhase('loading'); setMode(m);
    setSubject(subj ? subj.key : null);
    try {
      const r = await api.post('/academy/combine/generate', { mode: m, subject: subj ? subj.key : null }, { headers: auth() });
      setSessionId(r.data.session_id);
      setQuestions(r.data.questions || []);
      setGrade(r.data.grade);
      setStateLabel(r.data.state_label || '');
      setSubjectTitle(r.data.title || (subj ? subj.title : 'Practice'));
      setIdx(0); setSelected(null); setResult(null); setShowHint(false);
      setStreak(0); setBestStreak(0); setCorrectCount(0); setAccolade('');
      setPhase('play');
    } catch (e) {
      toast.error('Could not load a practice set. Try again.');
      setPhase('select');
    }
  };

  const q = questions[idx];

  const lockIn = async () => {
    if (selected === null || result) return;
    setBusy(true);
    try {
      const r = await api.post('/academy/combine/check', { session_id: sessionId, qid: q.id, given_index: selected }, { headers: auth() });
      setResult(r.data);
      if (r.data.correct) {
        const ns = streak + 1;
        setStreak(ns); setBestStreak((b) => Math.max(b, ns)); setCorrectCount((c) => c + 1);
        setAccolade(fireText(ns) || rand(CORRECT_CALLS));
      } else {
        setStreak(0);
        setAccolade(rand(MISS_CALLS));
      }
    } catch { toast.error('Could not check that answer.'); } finally { setBusy(false); }
  };

  const next = async () => {
    if (idx + 1 < questions.length) {
      setIdx(idx + 1); setSelected(null); setResult(null); setShowHint(false); setAccolade('');
      return;
    }
    // finish -> submit
    setBusy(true);
    try {
      const r = await api.post('/academy/combine/submit', { session_id: sessionId, responses: [], best_streak: bestStreak }, { headers: auth() });
      setSummary(r.data);
      setPhase('done');
      loadHistory();
    } catch { toast.error('Could not save your set.'); } finally { setBusy(false); }
  };

  // ---------- SELECT ----------
  if (phase === 'select') {
    return (
      <div className="min-h-screen bg-[#08080a] text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button onClick={() => nav('/academy/school')} className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-6" data-testid="combine-back">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Dumbbell className="w-7 h-7 text-orange-500" />
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">THE COMBINE</h1>
          </div>
          <p className="text-zinc-400 mb-2">Sports-centric reps to keep you sharp. Endless, grade-aligned, and it counts toward your Scholar Rating.</p>
          <p className="text-[11px] uppercase tracking-[0.3em] text-orange-500/80 font-black mb-6">GameDay Skill Engine · Tier 1</p>

          {/* Coach assignments (homework) */}
          {assignments.length > 0 && (
            <div className="mb-8" data-testid="combine-assignments">
              <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 font-black mb-3">From Your Coach</div>
              <div className="space-y-2">
                {assignments.map((a) => (
                  <div key={a.id} className={`flex items-center gap-3 border-2 rounded-xl p-3 ${a.done ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-emerald-500/50 bg-emerald-500/10'}`} data-testid={`assignment-${a.id}`}>
                    <Trophy className={`w-5 h-5 flex-shrink-0 ${a.done ? 'text-emerald-500' : 'text-emerald-400'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-black text-white">{a.subject_title}{a.note ? ` — ${a.note}` : ''}</div>
                      <div className="text-[11px] text-zinc-400">Assigned by {a.coach}{a.done ? ' · Completed ✓' : ''}</div>
                    </div>
                    {a.done
                      ? <span className="text-[10px] font-black uppercase text-emerald-400">Done</span>
                      : <button onClick={() => run({ m: a.mode, subj: a.subject ? SUBJECTS.find((s) => s.key === a.subject) : null })} className="bg-emerald-500 hover:bg-emerald-400 text-black px-3 py-2 text-[10px] font-black uppercase tracking-wider rounded" data-testid={`assignment-start-${a.id}`}>Start</button>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Training modes */}
          <div className="grid sm:grid-cols-3 gap-3 mb-8">
            <button onClick={() => run({ m: 'all' })} data-testid="combine-mode-all"
              className="group text-left border-2 border-orange-500/50 bg-orange-500/10 rounded-2xl p-4 hover:border-orange-500 transition-colors">
              <Dumbbell className="w-6 h-6 text-orange-500 mb-2" />
              <div className="font-black">All-Around Workout</div>
              <div className="text-xs text-zinc-400 mt-0.5">Mixed reps across all 4 subjects.</div>
            </button>
            <button onClick={() => run({ m: 'today' })} data-testid="combine-mode-today"
              className="group text-left border-2 border-zinc-800 rounded-2xl p-4 hover:border-blue-500/60 bg-black/40 transition-colors">
              <Calendar className="w-6 h-6 text-blue-400 mb-2" />
              <div className="font-black">Today's Lesson</div>
              <div className="text-xs text-zinc-400 mt-0.5">Reps on what the class covered today.</div>
            </button>
            <button onClick={() => run({ m: 'focus' })} data-testid="combine-mode-focus"
              className="group text-left border-2 border-zinc-800 rounded-2xl p-4 hover:border-emerald-500/60 bg-black/40 transition-colors">
              <Target className="w-6 h-6 text-emerald-400 mb-2" />
              <div className="font-black">Target a Weakness</div>
              <div className="text-xs text-zinc-400 mt-0.5">Attacks your lowest subject first.</div>
            </button>
          </div>

          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-600 font-black mb-3">Or drill a specific subject</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {SUBJECTS.map((s) => (
              <button key={s.key} onClick={() => run({ m: 'subject', subj: s })} data-testid={`combine-subject-${s.key}`}
                className="group text-left border-2 border-zinc-800 rounded-2xl p-5 hover:border-orange-500/60 transition-colors bg-black/40 relative overflow-hidden">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${s.color}22` }}>
                  <s.icon className="w-6 h-6" style={{ color: s.color }} />
                </div>
                <div className="text-lg font-black">{s.title}</div>
                <div className="text-sm text-zinc-500 mt-1">{s.blurb}</div>
                {averages.find((a) => a.subject === s.key) && (
                  <div className="mt-3 text-[11px] text-zinc-400">
                    Avg <span className="font-black text-white">{averages.find((a) => a.subject === s.key).avg}%</span>
                    <span className="text-zinc-600"> · {averages.find((a) => a.subject === s.key).sets} sets</span>
                  </div>
                )}
                <ArrowRight className="w-5 h-5 text-zinc-700 group-hover:text-orange-500 absolute top-5 right-5 transition-colors" />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---------- LOADING ----------
  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-[#08080a] flex items-center justify-center text-white">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-orange-500" />
          <p className="text-zinc-400 uppercase tracking-[0.3em] text-xs">Coach is drawing up your plays…</p>
        </div>
      </div>
    );
  }

  // ---------- DONE ----------
  if (phase === 'done' && summary) {
    const perfect = summary.correct === summary.total;
    return (
      <div className="min-h-screen bg-[#08080a] text-white flex items-center justify-center px-4" data-testid="combine-done">
        <div className="max-w-md w-full text-center">
          <div className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-5 ${perfect ? 'bg-orange-500/20' : 'bg-blue-500/20'}`}>
            {perfect ? <Flame className="w-10 h-10 text-orange-500" /> : <Trophy className="w-10 h-10 text-blue-400" />}
          </div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 font-black">Final Whistle · {subjectTitle}</div>
          <div className="text-6xl font-black my-3">{summary.score}%</div>
          <div className="text-zinc-400 mb-6">{summary.correct} of {summary.total} buckets · best streak {bestStreak}</div>
          <div className="flex items-center justify-center gap-2 mb-8 text-orange-400 font-black">
            <Zap className="w-5 h-5" /> +{summary.xp_awarded} XP {summary.xp_awarded === 0 && <span className="text-zinc-600 text-xs font-normal">(daily XP already banked — reps still count!)</span>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => run({ m: mode, subj: subject ? SUBJECTS.find((s) => s.key === subject) : null })} className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-black py-3.5 font-black text-xs uppercase tracking-widest rounded-lg" data-testid="combine-run-back">
              <RefreshCw className="w-4 h-4" /> Run It Back
            </button>
            <button onClick={() => setPhase('select')} className="border-2 border-zinc-800 hover:border-zinc-600 py-3.5 font-black text-xs uppercase tracking-widest rounded-lg" data-testid="combine-switch">
              Switch Subject
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- PLAY ----------
  const pct = ((idx) / questions.length) * 100;
  return (
    <div className={`min-h-screen text-white transition-colors duration-500 ${fire ? 'bg-gradient-to-b from-orange-950/40 to-[#08080a]' : 'bg-[#08080a]'}`} data-testid="combine-play">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* top bar */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-black">{subjectTitle} · G{grade}{stateLabel ? ` · ${stateLabel}` : ''}</div>
          <div className={`flex items-center gap-1.5 font-black text-sm ${fire ? 'text-orange-400' : 'text-zinc-500'}`} data-testid="combine-streak">
            {[0, 1, 2].map((i) => <Flame key={i} className={`w-4 h-4 ${streak > i ? 'text-orange-500' : 'text-zinc-700'} ${fire ? 'animate-pulse' : ''}`} />)}
            <span className="ml-1">{streak} streak</span>
          </div>
        </div>
        {/* progress */}
        <div className="h-1.5 bg-zinc-900 rounded-full mb-2 overflow-hidden">
          <div className="h-full bg-orange-500 rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-[11px] text-zinc-600 mb-5">Rep {idx + 1} of {questions.length}</div>

        {/* ON FIRE banner */}
        {fire && (
          <div className="mb-4 flex items-center gap-2 bg-orange-500/15 border border-orange-500/40 rounded-xl px-4 py-2.5 animate-pulse" data-testid="combine-onfire">
            <Flame className="w-5 h-5 text-orange-500" />
            <span className="font-black text-orange-300 tracking-wide text-sm">{fireText(streak)}</span>
          </div>
        )}

        {q && (
          <div className="border-2 border-zinc-800 rounded-2xl p-5 bg-black/40">
            {q.standard_code && <div className="text-[10px] font-mono text-zinc-600 mb-2">CA {q.standard_code} · {q.standard_text}</div>}
            <p className="text-lg font-semibold leading-relaxed mb-5" data-testid="combine-question">{q.question}</p>

            <div className="space-y-2.5">
              {q.choices.map((c, i) => {
                let cls = 'border-zinc-800 hover:border-zinc-600';
                if (result) {
                  if (i === result.answer_index) cls = 'border-emerald-500 bg-emerald-500/10';
                  else if (i === selected) cls = 'border-red-500 bg-red-500/10';
                  else cls = 'border-zinc-800 opacity-50';
                } else if (i === selected) cls = 'border-orange-500 bg-orange-500/10';
                return (
                  <button key={i} disabled={!!result} onClick={() => setSelected(i)} data-testid={`combine-choice-${i}`}
                    className={`w-full text-left border-2 rounded-xl px-4 py-3 text-sm transition-colors flex items-center gap-3 ${cls}`}>
                    <span className="w-6 h-6 rounded-full border border-current flex items-center justify-center text-[11px] font-black flex-shrink-0">{String.fromCharCode(65 + i)}</span>
                    <span className="flex-1">{c}</span>
                    {result && i === result.answer_index && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
                    {result && !result.correct && i === selected && <XCircle className="w-5 h-5 text-red-400" />}
                  </button>
                );
              })}
            </div>

            {/* coach hint */}
            {!result && (
              <button onClick={() => setShowHint((s) => !s)} className="mt-4 flex items-center gap-2 text-xs text-amber-400/90 hover:text-amber-300 font-bold" data-testid="combine-hint-toggle">
                <Lightbulb className="w-4 h-4" /> {showHint ? 'Hide' : 'Coach'} hint
              </button>
            )}
            {showHint && !result && <div className="mt-2 text-sm text-amber-200/80 bg-amber-500/5 border border-amber-500/20 rounded-lg p-3" data-testid="combine-hint">{q.coach_hint}</div>}

            {/* feedback */}
            {result && (
              <div className={`mt-4 rounded-xl p-4 ${result.correct ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`} data-testid="combine-feedback">
                <div className={`flex items-center gap-2 font-black text-sm ${result.correct ? 'text-emerald-300' : 'text-red-300'}`}>
                  {result.correct ? <Flame className="w-5 h-5 text-orange-500" /> : <Target className="w-5 h-5" />}
                  {accolade}
                </div>
                <p className="text-sm text-zinc-300 mt-2">{result.explanation}</p>
              </div>
            )}

            {/* action */}
            <div className="mt-5">
              {!result ? (
                <button onClick={lockIn} disabled={selected === null || busy}
                  className="w-full bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-black py-3.5 font-black text-xs uppercase tracking-[0.2em] rounded-lg flex items-center justify-center gap-2"
                  data-testid="combine-lock-in">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />} Lock It In
                </button>
              ) : (
                <button onClick={next} disabled={busy}
                  className="w-full bg-white hover:bg-zinc-200 text-black py-3.5 font-black text-xs uppercase tracking-[0.2em] rounded-lg flex items-center justify-center gap-2"
                  data-testid="combine-next">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{idx + 1 < questions.length ? 'Next Rep' : 'Finish Set'} <ArrowRight className="w-4 h-4" /></>}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
